import type { StoreRanking } from "@/api"
import { useSetState } from "ahooks-v2"
import { createModel } from "hox"
import { useCallback } from "react"
import { TYPE_WAYZ_POI } from "@/constants/constant"

interface stateVO {
  storeList: StoreRanking[] | null
  filterStoreList: StoreRanking[] | null
  // 用户网点总数
  storeTotal: number
  // 已解锁网点数
  storeUnlocked: number
  // 品牌未开通网点数
  lockedCount: number
  // 品牌已开通网点数
  unlockedCount: number
  // 网点类型: wayz,custom
  storeType: string
  // 自定义点位刷新凭据
  customRefreshCount: number
}

function useStoreList() {
  const [state, set] = useSetState<stateVO>({
    storeList: [],
    filterStoreList: [],
    storeTotal: 0,
    storeUnlocked: 0,
    lockedCount: 0,
    unlockedCount: 0,
    storeType: TYPE_WAYZ_POI,
    customRefreshCount: 0,
  })

  const setStoreList = useCallback(
    (value?: StoreRanking[] | ((prevState: stateVO) => Partial<stateVO>)) => {
      if (typeof value === "function") {
        set(value)
      } else {
        const storeTotal = value?.length || 0
        const storeUnlocked = value?.filter((item) => item.unlocked)?.length || 0
        set({
          storeList: value,
          storeTotal,
          storeUnlocked,
        })
      }
    },
    [set],
  )

  // 设置网点类型
  const setStoreType = useCallback(
    (value: string) => {
      set({
        storeType: value,
      })
    },
    [set],
  )

  // 设置自定义点位刷新凭据
  const setCustomRefreshCount = useCallback(
    (value: number | ((prevState: stateVO) => Partial<stateVO>)) => {
      if (typeof value === "function") {
        set(value)
      } else {
        set({
          customRefreshCount: value,
        })
      }
    },
    [set],
  )

  const setFilterStoreList = useCallback(
    (params: { unlocked?: boolean; locked?: boolean }) => {
      const { unlocked, locked } = params
      const filterLocked = (store: StoreRanking) => (locked === false ? store.unlocked : locked)

      const filterUnLocked = (store: StoreRanking) =>
        unlocked === false ? !store.unlocked : unlocked
      set((prevState) => {
        return {
          ...prevState,
          filterStoreList: prevState.storeList?.filter(filterLocked).filter(filterUnLocked),
        }
      })
    },
    [set],
  )

  const getStore = useCallback(
    (code: string) => {
      return state.storeList?.find((item) => item.outletscode === code)
    },
    [state.storeList],
  )

  // 设置品牌已开未开网点数
  const setBrandStoreCount = useCallback(
    ({ lockedCount, unlockedCount }: { lockedCount: number; unlockedCount: number }) => {
      set({
        lockedCount,
        unlockedCount,
      })
    },
    [set],
  )

  return {
    ...state,
    setStoreList,
    setFilterStoreList,
    getStore,
    setBrandStoreCount,
    setStoreType,
    setCustomRefreshCount,
  }
}

export default createModel(useStoreList)
